<script setup lang="ts">
import { ref, watch } from 'vue';
import { z } from 'zod';
import {
    UiForm,
    UiFormField,
    UiTextarea,
    UiCheckbox,
    UiButton,
    UiText,
    UiIcon,
    UiInfoBox,
    UiSelect,
    type UiSelectOption,
} from '@shopexperts/shared';
import StarRatingSelector from './StarRatingSelector.vue';
import TotalScore from './TotalScore.vue';
import ExpertDetailsCard, { type ExpertData } from './ExpertDetailsCard.vue';

export interface ReviewFormValues {
    quality: number;
    communication: number;
    timelines: number;
    value_for_money: number;
    review_text: string;
    project_value_range_id?: number | null;
    is_anonymous?: boolean;
    hired_on_shopexperts_override?: boolean;
}

const props = withDefaults(defineProps<{
    expert: ExpertData;
    showCloseButton?: boolean;
    isSubmitting?: boolean;
    isSuccess?: boolean;
    title?: string;
    reviewTextMaxLength?: number;
    description?: string;
    submitLabel?: string;
    successTitle?: string;
    successDescription?: string;
    initialValues?: ReviewFormValues;
    showHiredOverrideCheckbox?: boolean;
    hiredOverrideLabel?: string;
    showProjectValueRangeSelect?: boolean;
    projectValueRangeOptions?: UiSelectOption[];
    isLoadingProjectValueRanges?: boolean;
    projectValueRangesFailed?: boolean;
}>(), {
    showCloseButton: true,
    isSubmitting: false,
    isSuccess: false,
    title: 'Write your **review**',
    description: 'Provide a rating and a detailed review to reflect your experience with this expert.',
    submitLabel: 'Submit Review',
    successTitle: 'Review Submitted Successfully!',
    successDescription: 'Thank you for taking the time to share your feedback. Your review helps other clients make informed decisions.',
    showHiredOverrideCheckbox: false,
    hiredOverrideLabel: 'Override hired at Shopexperts',
    showProjectValueRangeSelect: false,
    projectValueRangeOptions: () => [],
    isLoadingProjectValueRanges: false,
    projectValueRangesFailed: false,
});

const emit = defineEmits<{
    submit: [values: ReviewFormValues];
    cancel: [];
    retryProjectValueRanges: [];
}>();

// Form model - initialize from props if provided
const formModel = ref<ReviewFormValues>({
    quality: props.initialValues?.quality ?? 0,
    communication: props.initialValues?.communication ?? 0,
    timelines: props.initialValues?.timelines ?? 0,
    value_for_money: props.initialValues?.value_for_money ?? 0,
    review_text: props.initialValues?.review_text ?? '',
    project_value_range_id: props.initialValues?.project_value_range_id ?? null,
    is_anonymous: props.initialValues?.is_anonymous ?? false,
    hired_on_shopexperts_override: props.initialValues?.hired_on_shopexperts_override ?? false,
});
const formInitialValues: ReviewFormValues = { ...formModel.value };

// Watch for initialValues changes (e.g., when editing different items)
watch(() => props.initialValues, (newValues) => {
    if (newValues) {
        formModel.value = {
            quality: newValues.quality,
            communication: newValues.communication,
            timelines: newValues.timelines,
            value_for_money: newValues.value_for_money,
            review_text: newValues.review_text,
            project_value_range_id: newValues.project_value_range_id ?? null,
            is_anonymous: newValues.is_anonymous ?? false,
            hired_on_shopexperts_override: newValues.hired_on_shopexperts_override ?? false,
        };
    }
}, { deep: true });

// Form schema
const schema = z.object({
    quality: z.number().min(1, 'Please rate quality of work').max(5),
    communication: z.number().min(1, 'Please rate communication').max(5),
    timelines: z.number().min(1, 'Please rate respect for timelines').max(5),
    value_for_money: z.number().min(1, 'Please rate value for money').max(5),
    review_text: z.string().min(10, 'Review must be at least 10 characters').max(props.reviewTextMaxLength ?? Infinity, 'Review must be at most 5000 characters'),
    project_value_range_id: z.number().nullable().optional(),
    is_anonymous: z.boolean().optional(),
    hired_on_shopexperts_override: z.boolean().optional(),
});

// Handle form submission - emit to parent
const handleSubmit = (values: ReviewFormValues) => {
    emit('submit', values);
};

// Reset form
const resetForm = () => {
    formModel.value = {
        quality: 0,
        communication: 0,
        timelines: 0,
        value_for_money: 0,
        review_text: '',
        project_value_range_id: null,
        is_anonymous: false,
        hired_on_shopexperts_override: false,
    };
};

defineExpose({ resetForm });
</script>

<template>
    <div v-if="isSuccess" class="flex flex-col items-center justify-center gap-4 py-8">
        <UiIcon name="check-circle-color" class="w-16 h-16" />
        <UiText as="p" variant="h3" class="text-se-gray-900 font-semibold text-center" :text="successTitle" />
        <UiText as="p" variant="p" class="text-se-gray-600 text-center" :text="successDescription" />
    </div>

    <UiForm v-else v-model="formModel" :schema="schema" :initial-values="formInitialValues" scroll-to-first-error @submit="handleSubmit">
        <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-4">
                <div class="flex items-center justify-between">
                    <UiText as="h3" variant="h3" class="text-se-gray-900" :text="title" />
                    <UiButton v-if="showCloseButton" variant="ghost" shape="square" @click="emit('cancel')">
                        <template #before_label>
                            <UiIcon name="close" class="w-6 h-6" />
                        </template>
                    </UiButton>
                </div>
                <UiText as="p" variant="p" class="text-se-gray-700" :text="description" />
            </div>

            <div class="flex flex-col gap-1 w-full">
                <UiText as="label" variant="caption-normal" class="text-se-gray-700" text="Review for" />
                <ExpertDetailsCard :expert="expert" />
            </div>

            <div class="flex flex-col gap-4">
                <UiFormField name="quality" label="" v-model="formModel.quality">
                    <template #default="{ modelValue, onUpdateModelValue }">
                        <StarRatingSelector :model-value="modelValue as number"
                            @update:model-value="onUpdateModelValue" label="Quality of Work" />
                    </template>
                </UiFormField>

                <UiFormField name="communication" label="" v-model="formModel.communication">
                    <template #default="{ modelValue, onUpdateModelValue }">
                        <StarRatingSelector :model-value="modelValue as number"
                            @update:model-value="onUpdateModelValue" label="Communication" />
                    </template>
                </UiFormField>

                <UiFormField name="timelines" label="" v-model="formModel.timelines">
                    <template #default="{ modelValue, onUpdateModelValue }">
                        <StarRatingSelector :model-value="modelValue as number"
                            @update:model-value="onUpdateModelValue" label="Respects Timelines" />
                    </template>
                </UiFormField>

                <UiFormField name="value_for_money" label="" v-model="formModel.value_for_money">
                    <template #default="{ modelValue, onUpdateModelValue }">
                        <StarRatingSelector :model-value="modelValue as number"
                            @update:model-value="onUpdateModelValue" label="Value for Money" />
                    </template>
                </UiFormField>

                <TotalScore :quality="formModel.quality" :communication="formModel.communication"
                    :timelines="formModel.timelines" :value-for-money="formModel.value_for_money" />
            </div>

            <UiFormField
                v-if="showProjectValueRangeSelect"
                name="project_value_range_id"
                label="Project value"
                :label-capitalize="false"
                v-model="formModel.project_value_range_id"
            >
                <template #default="{ modelValue, onUpdateModelValue, id, invalid, disabled }">
                    <div class="flex flex-col gap-2">
                        <UiInfoBox
                            v-if="projectValueRangesFailed"
                            variant="error"
                            title="Project value ranges could not be loaded"
                        >
                            <div class="flex flex-col items-start gap-2">
                                <UiText
                                    as="p"
                                    variant="p-small"
                                    text="The current value is preserved. Retry before choosing a different range."
                                />
                                <UiButton
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    label="Retry"
                                    :disabled="isLoadingProjectValueRanges"
                                    @click="emit('retryProjectValueRanges')"
                                />
                            </div>
                        </UiInfoBox>
                        <UiSelect
                            :model-value="modelValue === null || modelValue === undefined ? 'none' : String(modelValue)"
                            @update:model-value="(value: string | string[] | undefined) => onUpdateModelValue(value === 'none' || typeof value !== 'string' ? null : Number(value))"
                            :id="id"
                            :invalid="invalid"
                            :disabled="disabled || isLoadingProjectValueRanges || projectValueRangesFailed"
                            :options="projectValueRangeOptions"
                            placeholder="Select project value range"
                        />
                    </div>
                </template>
            </UiFormField>

            <!-- Review Text -->
            <UiFormField name="review_text" label="Write Your Review" v-model="formModel.review_text">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
                    <div class="flex flex-col gap-1">
                        <UiTextarea
                            placeholder="Please try to be as detailed as possible. Share your experience with this expert and help us make your next match even better ..."
                            :rows="6" :model-value="modelValue as string" @update:model-value="onUpdateModelValue"
                            @validate="onValidate" :id="id" :invalid="invalid" :disabled="disabled" />
                        <UiText v-if="error" as="p" variant="p-small" class="text-se-danger" :text="error" />
                        <UiText v-else as="p" variant="caption-normal" class="text-se-gray-700" text="This feedback is public and will be shared on the expert's profile." />
                    </div>
                </template>
            </UiFormField>

            <!-- Anonymous Checkbox -->
            <UiFormField name="is_anonymous" label="" v-model="formModel.is_anonymous">
                <UiCheckbox v-model="formModel.is_anonymous" label="Post as Anonymous" />
            </UiFormField>

            <UiFormField
                v-if="showHiredOverrideCheckbox"
                name="hired_on_shopexperts_override"
                label=""
                v-model="formModel.hired_on_shopexperts_override"
            >
                <UiCheckbox
                    v-model="formModel.hired_on_shopexperts_override"
                    :label="hiredOverrideLabel"
                />
            </UiFormField>

            <!-- Submit Button -->
            <div class="flex justify-end gap-3 pt-2">
                <UiButton v-if="showCloseButton" type="button" variant="outline" label="Cancel" @click="emit('cancel')" />
                <UiButton type="submit" variant="primary" :label="submitLabel" :disabled="isSubmitting" />
            </div>
        </div>
    </UiForm>
</template>
